import { Link, useLocation } from 'react-router-dom';
import {
  HStack,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Heading,
  MenuDivider,
  Text,
} from '@chakra-ui/react';
import { LogOut, Video, CircleHelp, GraduationCap } from 'lucide-react';
import { Txt } from '@duke-hq/app-provider-web/components/util/Txt';
import { useAuthTenant } from './useAuthTenant';
import { SidebarConsultant } from './SidebarTenant';
import { sizes } from '@duke-hq/ui/constants/layout';
import { UserAvatar } from '@duke-hq/ui/components/UserAvatar/UserAvatar';
import { IconLogo } from '@duke-hq/ui/components/Logo/IconLogo';
import { useTenantContext } from './TenantContext';
import {
  CourseReferralModal,
  CourseReferralButton,
} from '@duke-hq/app-provider-web/tenant/components/CourseReferralModal';
import { trpcTenant } from './trpcTenant';
import { toast } from '../components/ToastContainer';
import { BookCoachingCallModal } from './components/BookCoachingCallForms/BookCoachingCallModal';
import QuickReferralModal from './components/QuickReferralModal';
import { pxToRem } from '@duke-hq/ui/constants/scalable';

function useCurrentLocation() {
  const location = useLocation();
  const hostname = window.location?.hostname?.split('.')[0]?.toUpperCase() || '';

  return { pathname: location.pathname, hostname };
}

export function Navbar() {
  const auth = useAuthTenant();
  const tenantCtx = useTenantContext();
  const { pathname, hostname } = useCurrentLocation();
  const isTenantUsingNewReferralProcess = tenantCtx?.settings.uses_new_referral_process;
  const isOnDashboard = pathname === '/';

  const generateStudentAppMagicLink = trpcTenant.auth.generateStudentAppMagicLink.useMutation();

  const { data: memberInternalEnrolmentCount } =
    trpcTenant.enrolments.getMemberInternalEnrolmentCount.useQuery();

  return (
    <Flex
      alignItems="center"
      color="fg.emphasized"
      bg="bg.surface"
      borderBottomWidth="1px"
      position="relative"
      zIndex={1012}
      p="2"
      h={sizes.navbar}
      minH={sizes.navbar}
    >
      <HStack justify="space-between" spacing="8" h="full" w="full">
        <HStack spacing="10">
          <HStack spacing="3">
            <SidebarConsultant />
            <Link to="/">
              <Flex alignItems="center" justifyContent="center" gap={2}>
                <IconLogo h={pxToRem(32)} />
                <Heading size="xs">{auth.user.tenant.name || hostname}</Heading>
              </Flex>
            </Link>
          </HStack>
        </HStack>
        <HStack spacing={{ base: '2', md: '4' }}>
          <BookCoachingCallModal />
          {isOnDashboard ? (
            isTenantUsingNewReferralProcess ? (
              <CourseReferralButton />
            ) : (
              <QuickReferralModal />
            )
          ) : null}
          <CourseReferralModal />
          <Menu>
            <MenuButton>
              <UserAvatar user={auth.user} />
            </MenuButton>
            <MenuList>
              {isTenantUsingNewReferralProcess === true ? (
                <Link to="/how-to-refer-demo">
                  <MenuItem>
                    <Flex gap={4}>
                      <CircleHelp />
                      How to refer
                    </Flex>
                  </MenuItem>
                </Link>
              ) : null}
              <Link to="/student-demo">
                <MenuItem>
                  <Flex gap={4}>
                    <Video />
                    Student demo
                  </Flex>
                </MenuItem>
              </Link>
              <MenuDivider />
              {memberInternalEnrolmentCount !== undefined && memberInternalEnrolmentCount > 0 ? (
                <MenuItem
                  onClick={async () => {
                    if (auth.user.email !== null) {
                      const magicLink = await generateStudentAppMagicLink.mutateAsync();

                      if (magicLink !== undefined && magicLink.length > 0) {
                        window.open(magicLink, '_blank');
                        return;
                      }
                    }

                    toast({
                      status: 'error',
                      title: 'Error occurred while generating magic link',
                    });
                  }}
                >
                  <GraduationCap /> <Text ml={4}>Login to student app</Text>
                </MenuItem>
              ) : null}
              <MenuItem
                onClick={async () => {
                  await auth.signOut();
                }}
              >
                <LogOut /> <Txt ml={4}>Sign Out</Txt>
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </HStack>
    </Flex>
  );
}
